/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';
import { useQuery } from 'react-query';
import { LoadingOutlined } from '@ant-design/icons';
import { Form, Modal, Radio, Skeleton, Button } from 'antd-v5';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import './styles.scss';
import { Dropdown, Tooltip } from 'antd';
import moment from 'moment';
import { connect } from 'react-redux';
import NewActivityModal from './NewActivityModal';
import CheckboxDesc from '../../../../../Layout/CheckboxDesc';
import { clientbookApi } from '../../../../../../core/clientbook-api';
import { generatePresignedUrl, uploadToS3 } from '../../../../../../services/attachments';
import { notificationError, notificationSuccess } from '../../../../../../core/actions';
import { addRecentActivity } from '../../../../../../reducers/client';
import useSearchParams from '../../../../../../hooks/useSearchParams';
import truncateText from '../../../../../../utils/truncateText';
import ActivityItem from './components/activity-item';
import { getClientActivities, getClientPurchases } from '../../../../../../services/clients';
import { APPOINTMENTS_STATUS, getClientAppointments } from '../../../../../../services/appointments';
import QuickViewSkeletonCard from '../../../../../../componentsV2/Routes/Clients/QuickView/components/QuickViewSkeletonCard';

const ClientActivity = ({ activityList, client, auth, associateRefObj, refetchReminders, refetchAutoMessages }) => {
  const {
    data: activities,
    isFetching: isFetchingActivities,
    refetch: refetchActivities,
  } = useQuery(
    ['clientRecentActivity', client?.client_id],
    async () => {
      try {
        return await getClientActivities({
          clientId: client?.client_id,
          query: {
            offset: 0,
            limit: 500,
            sort: '-createdDate',
          },
          loadingStatusOff: true,
        });
      } catch (error) {
        console.error('Error fetching client recent activity:', error);
        return [];
      }
    },
    {
      enabled: !!client?.client_id,
      refetchOnWindowFocus: false,
      initialData: [],
      cacheTime: 0,
    },
  );
  const { data: purchases, isFetching: isFetchingPurchases } = useQuery(
    ['clientPurchases', client?.client_id],
    async () => {
      try {
        return await getClientPurchases({
          clientId: client?.client_id,
          query: {
            offset: 0,
            limit: 500,
            sort: '-transactionDateTime',
          },
          loadingStatusOff: true,
        });
      } catch (error) {
        console.error('Error fetching client purchases:', error);
        return [];
      }
    },
    {
      enabled: !!client?.client_id,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );
  const { data: appointments, isFetching: isFetchingAppointments } = useQuery(
    ['clientRecentAppointments', client?.client_id],
    async () => {
      try {
        const query = {
          limit: 200,
          offset: 0,
          statuses: [APPOINTMENTS_STATUS.COMPLETED, APPOINTMENTS_STATUS.CANCELED, APPOINTMENTS_STATUS.NO_SHOW],
          sort: '-createDateTime',
        };
        const data = await getClientAppointments(client?.client_id, { ...query }, true);
        return data?.data || [];
      } catch (error) {
        console.error('Error fetching client recent appointments:', error);
        return [];
      }
    },
    {
      enabled: !!client?.client_id,
      refetchOnWindowFocus: false,
      initialData: [],
    },
  );

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [list, setList] = useState([]);
  const [filteredList, setFilteredList] = useState([]);
  const [formattedList, setFormattedList] = useState([]);
  const [openAddNew, setOpenAddNew] = useState(false);
  const [clientActivity, setClientActivity] = useState([]);
  const { getValue, setValue } = useSearchParams();
  const activityFilter = getValue('activityFilter');
  const clientTab = getValue('clientTab');
  const addActivity = getValue('addActivity');

  useEffect(() => {
    if (addActivity === 'true' && clientTab === 'activity') {
      setOpenAddNew(true);
      setValue('addActivity', null);
    }
  }, [addActivity, clientTab]);
  const [openAssociateModal, setOpenAssociateModal] = useState(false);
  const [newAssociate, setNewAssociate] = useState(null);
  const [notes, setNotes] = useState(null);
  const [openNotesModal, setOpenNotesModal] = useState(false);
  const [attachments, setAttachments] = useState([]);

  useEffect(() => {
    const activitiesFiltered = activities?.map(act => ({
      id: act.id,
      activityType: 'activity',
      title: act.type?.name,
      subTitle: act.createdBy?.name,
      notes: act.notes,
      description: act.store?.name,
      created_date: format(act.createdDate, 'yyyyMMddHHmmss'),
      attachments: act.attachments,
      supportsAttachments: act.type?.supportsAttachments,
    }));
    const appointmentsFiltered =
      appointments?.map(apt => ({
        id: apt.id,
        activityType: 'appointment',
        title: `Appointment ${format(new Date(apt.appointmentDate), 'MM/dd/yyyy h:mmaaa')}`,
        subTitle: String(apt.associate?.firstName) + (apt.associate?.lastName ? ` ${apt.associate?.lastName}` : ''),
        notes: apt.note,
        description: apt.store?.name,
        created_date: format(apt.appointmentDate, 'yyyyMMddHHmmss'),
        status: apt.status,
      })) ?? [];
    const purchasesFiltered = purchases?.map(pur => ({
      id: pur.id,
      activityType: 'purchased',
      title: pur.productName,
      subTitle: pur.unit_price,
      description: pur?.associate_ref,
      created_date: pur.transaction_date_time,
      product: {
        name: pur.name,
        pos_ref_num: pur?.pos_ref_num || pur?.product_pos_ref_num,
      },
      transactionCodeTypeDisposition: pur?.transactionCode_type_disposition,
    }));
    const orderActivities = activities.sort((a, b) => {
      return (
        activities.filter(act => act.type?.name === b.type?.name).length -
        activities.filter(act => act.type?.name === a.type?.name).length
      );
    });
    const newActivity = orderActivities.reduce((acc, curr) => {
      if (acc.some(a => a.type?.name === curr.type?.name)) {
        return acc;
      }
      return [...acc, curr];
    }, []);
    setClientActivity(newActivity);
    setList([...activitiesFiltered, ...purchasesFiltered, ...appointmentsFiltered]);
    setFilteredList([...activitiesFiltered, ...purchasesFiltered, ...appointmentsFiltered]);
  }, [activities, purchases, appointments]);

  useEffect(() => {
    const datesObj = {};
    filteredList?.forEach(activity => {
      const date = +`${activity.created_date}`.slice(0, 8);
      if (!Array.isArray(datesObj[date])) {
        datesObj[date] = [];
      }
      datesObj[date].push(activity);
    });
    const dates = Object.entries(datesObj).map(([key, value]) => {
      return { date: moment(key, 'YYYYMMDD').toDate(), activities: value };
    });
    dates.forEach(activity => {
      activity.activities.sort((a, b) => {
        return b.created_date - a.created_date;
      });
    });
    dates.sort((a, b) => {
      return b.date - a.date;
    });
    setFormattedList(dates);
  }, [filteredList]);

  const selectItem = useCallback(
    value => {
      const selectedFilter = activityFilter.split(',');
      if (selectedFilter.includes(value.toString())) {
        const selectedItems = selectedFilter.filter(item => item !== value.toString());
        setValue('activityFilter', selectedItems);
      } else {
        const selectedItems = [...selectedFilter, value.toString()];
        setValue('activityFilter', selectedItems);
      }
    },
    [activities, activityFilter],
  );

  const saveActivityHandler = useCallback(
    async (activity = null, associateId = null, associateName = null) => {
      try {
        let attachmentsData = [];
        if (attachments.length > 0) {
          const uploadPromises = attachments.map(async attachment => {
            const { fileName, fileUrl, presignedUrl } = await generatePresignedUrl({
              fileType: attachment.file.type,
              attachmentType: 'activity',
            });
            await uploadToS3({ presignedUrl, file: attachment.file });
            return {
              fileName,
              fileType: attachment.file.type,
              url: fileUrl,
              description: attachment.description,
            };
          });
          attachmentsData = await Promise.all(uploadPromises);
        }
        const currActivity = activity || selectedActivity;
        const { client_id, store_id, store_name } = client;
        const noteCheck = notes?.length > 0 ? notes : null;
        await clientbookApi.post({
          url: `/clients/${client_id}/activities`,
          data: {
            typeId: currActivity.id,
            associateId,
            storeId: store_id,
            notes: noteCheck,
            attachments: attachmentsData.length > 0 ? attachmentsData : null,
          },
        });
        refetchActivities();
        notificationSuccess('Activity Saved!');
        addRecentActivity({
          client_id,
          createdBy: {
            id: associateId,
            name: associateName,
          },
          createdDate: new Date(),
          id: Math.floor(Math.random() * 1000000000),
          notes: noteCheck,
          store: {
            id: store_id,
            name: store_name,
          },
          type: { name: currActivity.description },
          attachments: attachmentsData.length > 0 ? attachmentsData : null,
        });
        refetchReminders();
        refetchAutoMessages();
      } catch (error) {
        console.log('🚀 ~ ClientActivity ~ error:', error);
        notificationError('Failed To Save Activity');
      } finally {
        setOpenAddNew(false);
        setAttachments([]);
      }
    },
    [attachments, notes, selectedActivity, activities, client, auth],
  );

  const handleClose = useCallback(() => {
    setOpenAddNew(false);
    setOpenNotesModal(false);
    setSelectedActivity(null);
    setNotes(null);
    setOpenAssociateModal(false);
    setNewAssociate(null);
  }, []);

  const handleActivityClick = useCallback(
    act => {
      setSelectedActivity(act);
      setNotes(null);
      if (!act.includeNotes) {
        if (act.remindersCount > 0 && client.associate_id && client.associate_id !== auth.userId) {
          setOpenAddNew(false);
          setOpenAssociateModal(true);
        } else {
          saveActivityHandler(act);
          handleClose();
        }
      } else {
        setOpenAddNew(false);
        setOpenNotesModal(true);
      }
    },
    [saveActivityHandler, client, auth],
  );

  const items = useMemo(() => {
    return activityList.map(act => ({
      key: act.id,
      label:
        act.description.length > 20 ? (
          <Tooltip
            title={act.description}
            overlayInnerStyle={{ borderRadius: '10px', padding: '8px 12px 0px 12px', backgroundColor: '#454545' }}
          >
            {truncateText(act.description, 20, { suffix: '...' })}
          </Tooltip>
        ) : (
          act.description
        ),
      onClick: () => handleActivityClick(act),
    }));
  }, [activityList]);

  const itemsFilter = useMemo(() => {
    const newItems = clientActivity.map(act => ({
      key: act.id,
      disabled: false,
      label: (
        <CheckboxDesc
          key={act.id}
          checked={!!activityFilter.split(',').find(i => i.toString() === act.type?.name.toString())}
          onclick={() => selectItem(act.type?.name)}
          text={<div className="checkboxItem">{act.type?.name}</div>}
          classProps="w-100-P"
          checkButtonProps="m-top-4 m-btm-4 w-100-P"
        />
      ),
    }));

    return [
      ...newItems,
      {
        key: 'purchase',
        disabled: false,
        label: (
          <CheckboxDesc
            key="purchase"
            checked={!!activityFilter.split(',').find(i => i.toString() === 'purchased')}
            onclick={() => selectItem('purchased')}
            text={<div className="checkboxItem">Purchase history</div>}
            classProps="w-100-P"
            checkButtonProps="m-top-4 m-btm-4 w-100-P"
          />
        ),
      },
    ];
  }, [clientActivity, activityFilter]);

  const handleApplyFilter = useCallback(
    ({ clear = false }) => {
      const selectedFilter = activityFilter.split(',');
      setOpenFilter(false);
      let filtered = [];
      if (selectedFilter.length === 0 || clear || activityFilter === '') {
        filtered = list;
        setValue('activityFilter', null);
      } else {
        filtered = list.filter(act => {
          return selectedFilter.includes(act.activityType) || selectedFilter.includes(act.title);
        });
      }
      setFilteredList(filtered);
    },
    [activityFilter, list],
  );

  const handleClearAll = useCallback(() => {
    setValue('activityFilter', null);
    handleApplyFilter({ clear: true });
  }, [handleApplyFilter]);

  useEffect(() => {
    if (activityFilter && list?.length > 0) {
      handleApplyFilter({ clear: false });
    } else {
      handleApplyFilter({ clear: true });
    }
  }, [list, clientTab]);

  const handleSaveByNotesModal = useCallback(() => {
    if (selectedActivity.remindersCount > 0 && client.associate_id && client.associate_id !== auth.userId) {
      setOpenAssociateModal(true);
    } else {
      saveActivityHandler();
      handleClose();
    }
  }, [saveActivityHandler, selectedActivity, notes, auth, client]);

  const handleSave = useCallback(() => {
    saveActivityHandler(null, newAssociate?.id, newAssociate?.name);
    handleClose();
  }, [saveActivityHandler, selectedActivity, notes, newAssociate]);

  const handleNewAssociate = useCallback(
    value => {
      if (value === auth.userId) {
        setNewAssociate({ id: auth.userId, name: `${auth?.firstName || ''} ${auth?.lastName || ''}` });
      } else {
        setNewAssociate({
          id: client.associate_id,
          name: `${client?.associate?.first_name || ''} ${client?.associate?.last_name || ''}`,
        });
      }
    },
    [auth, client],
  );

  useEffect(() => {
    setNewAssociate({
      id: client.associate_id,
      name: `${client?.associate?.first_name || ''} ${client?.associate?.last_name || ''}`,
    });
  }, [client.associate_id]);

  return (
    <div
      className="fs-15 fw-500 m-btm-41 flex-col-left p-left-21 p-right-33"
      data-testid="clients__activity--root"
      style={{ alignItems: 'stretch', overflow: 'hidden' }}
    >
      <div className="flex-row-spacebetween-nowrap m-btm-8" style={{ alignItems: 'center', width: '100%' }}>
        <div className="flex-row-center">
          <p>Client Activity</p>
          <Dropdown
            menu={{ items }}
            placement="bottomRight"
            trigger={['click']}
            onOpenChange={e => setOpenAddNew(e)}
            open={openAddNew}
            arrow
            align={{
              offset: [16, 0],
            }}
          >
            <FontAwesomeIcon
              icon="fa-regular fa-plus"
              className="m-left-13 pointer"
              data-testid="clients__activity--add-button"
            />
          </Dropdown>
        </div>
        <div className="flex-row-center">
          {isFetchingActivities ? (
            <LoadingOutlined className="m-right-16" style={{ fontSize: 16 }} spin />
          ) : (
            <Dropdown
              menu={{ items: itemsFilter }}
              placement="bottomRight"
              trigger={['click']}
              open={openFilter}
              onOpenChange={e => setOpenFilter(e)}
              // eslint-disable-next-line react/no-unstable-nested-components
              dropdownRender={menu => (
                <div
                  className="ant-dropdown-menu cd-dropdown-menu-filter scrollbar-y"
                  style={{ padding: 16, borderRadius: 10 }}
                >
                  <div className="cd-dropdown-menu-filter-header">
                    <p>Filter by</p>
                    {!!activityFilter && activityFilter.split(',')?.length > 0 && (
                      <span className="pointer" onClick={handleClearAll}>
                        Clear All
                      </span>
                    )}
                  </div>
                  {React.cloneElement(menu, { style: { boxShadow: 'none', border: 0 } })}
                  <Button
                    type="primary"
                    size="large"
                    style={{ width: '153px' }}
                    className="m-btm-8 m-top-8"
                    onClick={handleApplyFilter}
                  >
                    APPLY FILTER
                  </Button>
                </div>
              )}
              arrow
              align={{
                offset: [16, 0],
              }}
            >
              {activityFilter?.length === 0 ? (
                <FontAwesomeIcon icon="fa-regular fa-filter" className="pointer" />
              ) : (
                <img
                  src="https://dashboard-v2-images.s3.amazonaws.com/FilterActive.svg"
                  alt="non-default filter icon"
                  className="pointer"
                  style={{ height: '16px', width: '16px' }}
                />
              )}
            </Dropdown>
          )}
        </div>
      </div>
      {isFetchingActivities || isFetchingPurchases || isFetchingAppointments ? (
        <div className="flex-col-left gap-16 m-btm-41" style={{ alignItems: 'stretch' }}>
          <Skeleton active paragraph={{ rows: 1 }} title={false} />
          {Array.from({ length: 3 }).map((_, index) => (
            <QuickViewSkeletonCard key={index} />
          ))}
        </div>
      ) : filteredList?.length > 0 ? (
        <div className="flex-col-left scrollbar-y" style={{ alignItems: 'stretch' }}>
          {formattedList.map(date =>
            date.activities.length > 0 ? (
              <div key={format(date.date, 'yyyyMMdd')}>
                <div className="fs-14 fw-500 align-left m-btm-15">
                  <FontAwesomeIcon icon="fa-light fa-calendar" className="m-right-8" />
                  {format(date.date, 'MMMM dd')}
                </div>
                {date.activities.map(activity => (
                  <ActivityItem
                    key={activity.id}
                    role={auth.role}
                    recentActivity={client.recentActivity}
                    client_id={client.client_id}
                    associateRefObj={associateRefObj}
                    {...activity}
                    onSuccessSave={refetchActivities}
                  />
                ))}
              </div>
            ) : null,
          )}
        </div>
      ) : (
        <div className="fw-400 fs-12 align-left m-top-4" style={{ color: '#818181' }}>
          No Client Activity
        </div>
      )}
      {openNotesModal && (
        <NewActivityModal
          activity={selectedActivity}
          onClose={handleClose}
          associateId={auth?.userId}
          associateName={`${auth?.firstName || ''} ${auth?.lastName || ''}`}
          merchantId={auth?.merchantId}
          activities={activities}
          onSaveActivity={handleSaveByNotesModal}
          fileData={attachments}
          notes={notes}
          setNotes={setNotes}
          attachments={attachments}
          setAttachments={setAttachments}
        />
      )}
      <Modal
        open={openAssociateModal}
        onCancel={() => {
          setOpenAssociateModal(false);
        }}
        centered
        title="There are reminders for this client activity"
        onOk={handleSave}
        okText="Save"
        closable={false}
      >
        <Form.Item className="m-top-16" label="Who should own the client activity?" layout="vertical">
          <Radio.Group
            onChange={e => handleNewAssociate(e.target.value)}
            value={newAssociate?.id}
            defaultValue={client.associate_id}
          >
            <Radio value={client.associate_id} className="w-100-P">
              {`${client?.associate?.first_name || ''} ${client?.associate?.last_name || ''} (Assigned associate)`}
            </Radio>
            <Radio value={auth.userId}>{`${auth?.firstName || ''} ${auth?.lastName || ''} (you)`}</Radio>
          </Radio.Group>
        </Form.Item>
      </Modal>
    </div>
  );
};

const mapStateToProps = state => ({ auth: state.auth });

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(ClientActivity);
